import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import classname from 'classnames';
import { GlobalFocusToggle } from '@els/els-react--utils';
import { Loader } from '@els/els-react--loader';
import AppStorage from '@Services/AppStorage';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import * as WindowHelper from '@Services/WindowHelper';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { MultiBackend } from 'react-dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';

import SidebarPanel from '../SearchSection/SidebarPanel';
import SearchSection from '../SearchSection';
import Controls from '../Controls/Controls';
import QueryPanel from '../QueryPanel';
import HeaderContainer from '../HeaderContainer';
import { TargetModeProvider } from '../TargetModeContext';

import './QueryContainer.scss';

/* eslint-disable no-shadow */
function QueryContainer({
  nodesList,
  properties,
  favList,
  hiddenList,
  forms,
  indexedQuerylets,
  indexedForms,
  importedQuery,
  initNodeList,
  addToBuilderAction,
  mergeNodeItemAction,
  onToggleQueryletAction,
  getFormsAction,
  cleanupOnUnmountAction,
  importFormId,
  autoSearch,
}) {
  const qbPanelsStates = AppStorage.instance.get('qbPanelsStates') || {};
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(
    qbPanelsStates.sidebar ? qbPanelsStates.sidebar : WindowHelper.isBreakpointView()
  );
  const location = useLocation();
  const importedQueryStateParamValue = location?.state?.importedQuery;

  useEffect(() => {
    initNodeList();
    RouteChangeTracking.trackOnRouteChangeSuccess();
    const skipLinkFooter = document.getElementById('els-skiplink-footer');
    const footerListItem = skipLinkFooter ? skipLinkFooter.parentElement : null;
    if (footerListItem) {
      footerListItem.style.display = 'none'; // Hide the element that contains the skip link for footer
    }
    return () => {
      cleanupOnUnmountAction();
      if (footerListItem) {
        footerListItem.style.display = ''; // Reset display to show the footer element again
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getFormsAction(importFormId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importFormId]);

  const addToBuilderFn = (node) => {
    addToBuilderAction(node);
  };

  return (
    <GlobalFocusToggle>
      {autoSearch && <Loader isVisible />}
      <TargetModeProvider nodesList={nodesList} addToBuilderAction={addToBuilderAction} mergeNodeItemAction={mergeNodeItemAction}>
        <DndProvider backend={MultiBackend} options={HTML5toTouch}>
          <div
            className={classname('query-builder', {
              'query-builder--collapsed-sidebar': isSidebarCollapsed,
            })}>
            <header className="query-builder__header">
              <Controls
                nodesList={nodesList}
                favList={favList}
                hiddenList={hiddenList}
                properties={properties}
                importedQuery={importedQuery}
                importedQueryStateParamValue={importedQueryStateParamValue}
                addToBuilder={addToBuilderFn}
              />
            </header>
            <div className="query-builder__main">
              <QueryPanel
                className="query-builder__content"
                aria-label="Query Builder Stage"
                nodesList={nodesList}
                onToggleQuerylet={onToggleQueryletAction}
              />
              <div className="query-builder__panel-footer">
                <HeaderContainer nodesList={nodesList} properties={properties} isSidebarCollapsed={isSidebarCollapsed} />
              </div>
            </div>
          </div>
          <SidebarPanel isSidebarCollapsed={isSidebarCollapsed} setSidebarCollapsed={setSidebarCollapsed}>
            <SearchSection
              properties={properties}
              forms={forms}
              getForms={getFormsAction}
              indexedQuerylets={indexedQuerylets}
              indexedForms={indexedForms}
              addToBuilder={addToBuilderFn}
            />
          </SidebarPanel>
        </DndProvider>
      </TargetModeProvider>
    </GlobalFocusToggle>
  );
}

QueryContainer.propTypes = {
  nodesList: PropTypes.array,
  properties: PropTypes.array,
  favList: PropTypes.array,
  indexedQuerylets: PropTypes.object,
  indexedForms: PropTypes.object,
  hiddenList: PropTypes.array,
  importedQuery: PropTypes.any,
  forms: PropTypes.array.isRequired,
  initNodeList: PropTypes.func,
  addToBuilderAction: PropTypes.func,
  mergeNodeItemAction: PropTypes.func,
  getFormsAction: PropTypes.func.isRequired,
  cleanupOnUnmountAction: PropTypes.func.isRequired,
  onToggleQueryletAction: PropTypes.func,
  importFormId: PropTypes.any,
  autoSearch: PropTypes.string,
};

export default QueryContainer;
